/*
 * AdminRecipeDiscovery — FoodPulse Recipe Discovery Engine dashboard.
 *
 * Route: /admin/recipe-discovery
 *
 * Tabs:
 *   Add            — paste a single URL + optional notes/hints
 *   Bulk           — paste many URLs (one per line) or import from CSV
 *   Queue          — processing funnel by status + by platform
 *   Sources        — searchable/filterable library of every submitted URL
 *   Creators       — creator library (channel/account rollup)
 *
 * Row actions per source:
 *   Reprocess      — re-run the adapter (updates title/creator/thumb)
 *   Publish        — turn schema.org Recipe source into a published recipe
 *   Reject         — mark as not-a-recipe
 *   View original  — external link
 */

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import {
  Link2, Upload, ListChecks, Users, LayoutDashboard, RefreshCw, CheckCircle2,
  Ban, ExternalLink, AlertTriangle, Youtube, Search, Filter, X, Sparkles, Edit3,
} from "lucide-react";
import RecipeSourceEditor from "@/components/RecipeSourceEditor";

const STATUS_LABEL = {
  queued: "Queued", fetching: "Fetching", analyzing: "Analyzing",
  recipe_detected: "Recipe detected", not_a_recipe: "Not a recipe",
  extracting: "Extracting", researching: "Researching",
  creating_article: "Creating article", needs_review: "Needs review",
  needs_manual_metadata: "Needs manual metadata",
  ready: "Ready", published: "Published", duplicate: "Duplicate",
  access_restricted: "Access restricted", source_unavailable: "Source unavailable",
  failed: "Failed", archived: "Archived",
};

const STATUS_TONE = {
  recipe_detected: "#3E5A2A", ready: "#3E5A2A", published: "#3E5A2A",
  needs_review: "#8A5A24", duplicate: "#8A5A24",
  needs_manual_metadata: "#8A5A24",
  access_restricted: "#B04A3E", source_unavailable: "#B04A3E",
  not_a_recipe: "#8A6E4E", failed: "#B04A3E",
};

const PLATFORM_ICON = {
  youtube: "▶", tiktok: "♪", instagram: "◈", facebook: "f", web: "◫",
};

function Chip({ children, tone = "#5C6B52", data = {} }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] border"
          style={{ borderColor: tone, color: tone, background: `${tone}12` }} {...data}>
      {children}
    </span>
  );
}

export default function AdminRecipeDiscovery() {
  const [tab, setTab] = useState("add");
  const [queue, setQueue] = useState(null);

  const loadQueue = () => {
    http.get("/recipe-discovery/queue").then((r) => setQueue(r.data)).catch(() => {});
  };
  useEffect(loadQueue, []);

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8" data-testid="admin-recipe-discovery">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>
            <Sparkles size={13} className="inline mr-1.5 -mt-0.5" />
            RECIPE DISCOVERY ENGINE
          </div>
          <h1 className="font-display text-3xl md:text-4xl leading-tight">Global recipe discovery</h1>
          <p className="text-sm mt-1 max-w-[64ch]" style={{ color: "var(--wofy-text-soft)" }}>
            Paste any public recipe / video URL. FoodPulse stores the source, extracts factual metadata,
            attributes the creator, and adds it to the review queue. We never re-host copyrighted media.
          </p>
        </div>
        <button onClick={loadQueue}
                className="inline-flex items-center gap-1.5 text-sm border rounded-full px-3 py-1.5"
                style={{ borderColor: "var(--wofy-line)" }}
                data-testid="refresh-queue-btn">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Queue funnel */}
      {queue && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="queue-funnel">
          <div className="rounded-xl border p-4" style={{ borderColor: "var(--wofy-line)" }}>
            <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">Sources</div>
            <div className="font-display text-2xl">{queue.total_sources}</div>
          </div>
          {["recipe_detected", "needs_review", "access_restricted", "duplicate", "published"].map((k) => (
            <div key={k} className="rounded-xl border p-4" style={{ borderColor: "var(--wofy-line)" }}>
              <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">{STATUS_LABEL[k]}</div>
              <div className="font-display text-2xl" style={{ color: STATUS_TONE[k] || "var(--wofy-text)" }}>
                {queue.by_status[k] || 0}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b mb-6" style={{ borderColor: "var(--wofy-line)" }}>
        {[
          { k: "add", label: "Add URL", icon: Link2 },
          { k: "bulk", label: "Bulk import", icon: Upload },
          { k: "queue", label: "Sources", icon: ListChecks },
          { k: "creators", label: "Creators", icon: Users },
          { k: "dashboard", label: "By platform", icon: LayoutDashboard },
        ].map(({ k, label, icon: Icon }) => (
          <button key={k} onClick={() => setTab(k)}
                  data-testid={`tab-${k}`}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm border-b-2 -mb-px ${
                    tab === k ? "font-semibold" : "opacity-70 hover:opacity-100"
                  }`}
                  style={{ borderColor: tab === k ? "var(--wofy-primary)" : "transparent",
                           color: tab === k ? "var(--wofy-primary)" : "var(--wofy-text)" }}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {tab === "add"      && <AddURLTab onDone={loadQueue} />}
      {tab === "bulk"     && <BulkURLTab onDone={loadQueue} />}
      {tab === "queue"    && <SourcesTab onChange={loadQueue} />}
      {tab === "creators" && <CreatorsTab />}
      {tab === "dashboard"&& <PlatformDashboardTab queue={queue} />}
    </div>
  );
}

function AddURLTab({ onDone }) {
  const [url, setUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [hintFood, setHintFood] = useState("");
  const [hintCuisine, setHintCuisine] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!url.trim()) return;
    setBusy(true); setErr(""); setResult(null);
    try {
      const r = await http.post("/recipe-discovery/sources", {
        url: url.trim(), notes_for_ai: notes, hint_food_slug: hintFood || null,
        hint_cuisine: hintCuisine || null,
      });
      setResult(r.data.source);
      // Auto-open the full editor so the admin can enrich + publish immediately.
      setEditorOpen(true);
      onDone && onDone();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const refreshSource = async () => {
    if (!result?.id) return;
    try {
      const r = await http.get(`/recipe-discovery/sources/${result.id}`);
      setResult(r.data);
    } catch {}
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="rounded-2xl border p-5" style={{ borderColor: "var(--wofy-line)" }}>
        <div className="text-[11px] font-mono uppercase tracking-widest opacity-70 mb-2">
          Create FoodPulse recipe from link
        </div>
        <input type="url" value={url} onChange={(e) => setUrl(e.target.value)}
               data-testid="single-url-input"
               placeholder="Paste recipe / video / social post URL"
               className="w-full border rounded-lg px-3 py-2.5 text-sm" style={{ borderColor: "#DCEAD1" }} />
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                  data-testid="single-notes-input"
                  placeholder="Notes for AI (optional) — e.g. Concentrate on the Kerala-style fish preparation."
                  rows={3}
                  className="w-full border rounded-lg px-3 py-2.5 text-sm mt-3" style={{ borderColor: "#DCEAD1" }} />
        <div className="grid grid-cols-2 gap-2 mt-3">
          <input value={hintFood} onChange={(e) => setHintFood(e.target.value)}
                 placeholder="Hint food slug (e.g. banana)"
                 className="border rounded-lg px-3 py-2 text-xs" style={{ borderColor: "#DCEAD1" }} />
          <input value={hintCuisine} onChange={(e) => setHintCuisine(e.target.value)}
                 placeholder="Hint cuisine (e.g. Kerala)"
                 className="border rounded-lg px-3 py-2 text-xs" style={{ borderColor: "#DCEAD1" }} />
        </div>
        <button onClick={submit} disabled={busy || !url.trim()}
                data-testid="single-submit-btn"
                className="mt-4 px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50"
                style={{ background: "var(--wofy-primary)", color: "white" }}>
          {busy ? "Analyzing…" : "Analyze & save source"}
        </button>
        {err && <div className="mt-3 text-sm" style={{ color: "#B04A3E" }} data-testid="single-error">{err}</div>}
      </div>
      <div className="rounded-2xl border p-5" style={{ borderColor: "var(--wofy-line)", background: "#FBFBF6" }}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] font-mono uppercase tracking-widest opacity-70">Result</div>
          {result && (
            <button onClick={() => setEditorOpen(true)}
                    className="inline-flex items-center gap-1 text-xs border rounded-full px-3 py-1 hover:bg-white"
                    style={{ borderColor: "#DCEAD1" }}
                    data-testid="single-open-editor">
              <Edit3 size={11} /> Open editor
            </button>
          )}
        </div>
        {!result && <p className="text-sm opacity-70">Submitted sources will appear here with the extracted metadata.</p>}
        {result && <SourceCard s={result} onChange={refreshSource} />}
      </div>
      {editorOpen && result && (
        <RecipeSourceEditor
          source={result}
          variant="modal"
          onClose={() => setEditorOpen(false)}
          onSaved={refreshSource}
          onPublished={() => { setEditorOpen(false); refreshSource(); onDone && onDone(); }}
        />
      )}
    </div>
  );
}

function BulkURLTab({ onDone }) {
  const [text, setText] = useState("");
  const [notes, setNotes] = useState("");
  const [hintFood, setHintFood] = useState("");
  const [hintCuisine, setHintCuisine] = useState("");
  const [busy, setBusy] = useState(false);
  const [batch, setBatch] = useState(null);

  const parseUrls = () => {
    return Array.from(new Set(
      text.split(/\r?\n|,/).map((s) => s.trim()).filter((s) => /^https?:\/\//i.test(s))
    ));
  };

  const submit = async () => {
    const urls = parseUrls();
    if (!urls.length) return;
    setBusy(true); setBatch(null);
    try {
      const r = await http.post("/recipe-discovery/sources/bulk", {
        urls, notes_for_ai: notes, hint_food_slug: hintFood || null,
        hint_cuisine: hintCuisine || null,
      });
      setBatch(r.data);
      onDone && onDone();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const onCSVFile = async (ev) => {
    const f = ev.target.files?.[0];
    if (!f) return;
    const txt = await f.text();
    setText(txt);
  };

  const urlCount = parseUrls().length;

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-3 rounded-2xl border p-5" style={{ borderColor: "var(--wofy-line)" }}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] font-mono uppercase tracking-widest opacity-70">Bulk recipe import</div>
          <label className="text-xs cursor-pointer border rounded-full px-3 py-1"
                 style={{ borderColor: "#DCEAD1" }}>
            <input type="file" accept=".csv,.txt,.tsv" onChange={onCSVFile} className="hidden" data-testid="bulk-file-input" />
            Import file
          </label>
        </div>
        <textarea data-testid="bulk-urls-input"
                  value={text} onChange={(e) => setText(e.target.value)}
                  rows={12}
                  placeholder={"Paste URLs (one per line)\nhttps://youtube.com/...\nhttps://facebook.com/...\nhttps://instagram.com/...\nhttps://example.com/recipe/..."}
                  className="w-full border rounded-lg p-3 text-sm font-mono" style={{ borderColor: "#DCEAD1" }} />
        <div className="grid grid-cols-2 gap-2 mt-3">
          <input value={hintFood} onChange={(e) => setHintFood(e.target.value)}
                 placeholder="Hint food slug (applies to all)"
                 className="border rounded-lg px-3 py-2 text-xs" style={{ borderColor: "#DCEAD1" }} />
          <input value={hintCuisine} onChange={(e) => setHintCuisine(e.target.value)}
                 placeholder="Hint cuisine (applies to all)"
                 className="border rounded-lg px-3 py-2 text-xs" style={{ borderColor: "#DCEAD1" }} />
        </div>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  placeholder="Notes for AI (applies to all)"
                  className="w-full border rounded-lg px-3 py-2.5 text-sm mt-3" style={{ borderColor: "#DCEAD1" }} />
        <div className="flex items-center gap-3 mt-4">
          <button data-testid="bulk-submit-btn"
                  onClick={submit} disabled={busy || urlCount === 0}
                  className="px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50"
                  style={{ background: "var(--wofy-primary)", color: "white" }}>
            {busy ? "Analyzing…" : `Analyze ${urlCount} URL${urlCount === 1 ? "" : "s"}`}
          </button>
          <span className="text-xs opacity-70">Duplicates are auto-detected and skipped.</span>
        </div>
      </div>
      <div className="lg:col-span-2 rounded-2xl border p-5" style={{ borderColor: "var(--wofy-line)", background: "#FBFBF6" }}>
        <div className="text-[11px] font-mono uppercase tracking-widest opacity-70 mb-2">Batch result</div>
        {!batch && <p className="text-sm opacity-70">Submit URLs to see per-URL extraction status here.</p>}
        {batch && (
          <div className="text-sm space-y-2" data-testid="bulk-result">
            <div>Batch id: <code className="text-xs">{batch.batch_id}</code></div>
            <div>Processed: <strong>{batch.count}</strong> URL{batch.count === 1 ? "" : "s"}</div>
            <ul className="mt-2 space-y-1 max-h-96 overflow-auto text-xs">
              {batch.results.map((r, i) => (
                <li key={i} className="border-b pb-1" style={{ borderColor: "#F2ECDE" }}>
                  <div><Chip tone={STATUS_TONE[r.status] || "#5C6B52"}>{STATUS_LABEL[r.status] || r.status}</Chip> · {r.platform}</div>
                  <div className="opacity-70 mt-0.5">{r.title || r.original_url || r.error}</div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function SourceCard({ s, compact = false, onChange }) {
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(!compact);
  const [enrichOpen, setEnrichOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);

  const doAction = async (fn, label) => {
    if (!window.confirm(`${label}?`)) return;
    setBusy(true);
    try { await fn(); onChange && onChange(); }
    catch (e) { alert(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  const reprocess = () => doAction(
    () => http.post(`/recipe-discovery/sources/${s.id}/reprocess`), "Re-run adapter on this URL");
  const reject = () => doAction(
    () => http.post(`/recipe-discovery/sources/${s.id}/reject`), "Mark this source as not-a-recipe");

  return (
    <div className="rounded-xl border p-3 bg-white" style={{ borderColor: "var(--wofy-line)" }}
         data-testid={`source-${s.id}`}>
      <div className="flex items-start gap-3">
        {s.thumbnail_url ? (
          <img src={s.thumbnail_url} alt="" className="w-24 h-16 rounded object-cover flex-shrink-0"
               onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          <div className="w-24 h-16 rounded flex items-center justify-center text-2xl flex-shrink-0"
               style={{ background: "#F2ECDE" }}>{PLATFORM_ICON[s.platform] || "◫"}</div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Chip tone={STATUS_TONE[s.status] || "#5C6B52"}>{STATUS_LABEL[s.status] || s.status}</Chip>
            <Chip>{s.platform}{s.source_type ? ` · ${s.source_type}` : ""}</Chip>
            {s.schema_recipe && <Chip tone="#3E5A2A">schema.org Recipe</Chip>}
            {s.copyright_ok && <Chip tone="#3E5A2A">Publisher-indexed</Chip>}
            {s.fetch_error && <Chip tone="#B04A3E">{s.fetch_error}</Chip>}
          </div>
          <div className="font-display text-sm mt-1 line-clamp-2">{s.title || "(no title extracted)"}</div>
          {s.creator_name && (
            <div className="text-xs opacity-70 mt-0.5">by {s.creator_name}{s.channel_name && s.channel_name !== s.creator_name ? ` · ${s.channel_name}` : ""}</div>
          )}
          <div className="text-xs opacity-60 mt-0.5 truncate">
            <a href={s.canonical_url || s.original_url} target="_blank" rel="noopener noreferrer" className="underline hover:opacity-100">
              {s.canonical_url || s.original_url}
            </a>
          </div>
        </div>
      </div>
      {expanded && s.description && (
        <div className="text-xs mt-2 opacity-80 leading-relaxed">{s.description}</div>
      )}
      {expanded && s.schema_recipe && (
        <div className="mt-2 text-[11px] opacity-80">
          <strong>Structured recipe:</strong> {(s.schema_recipe.recipeIngredient || []).length} ingredients ·
          {" "}{Array.isArray(s.schema_recipe.recipeInstructions) ? s.schema_recipe.recipeInstructions.length : "?"} steps ·
          {" "}yields {s.schema_recipe.recipeYield || "?"}
        </div>
      )}
      <div className="flex items-center gap-1 mt-3 flex-wrap">
        <a href={s.canonical_url || s.original_url} target="_blank" rel="noopener noreferrer"
           className="inline-flex items-center gap-1 text-xs border rounded-full px-3 py-1.5 hover:bg-[#F5FAF0]"
           style={{ borderColor: "#DCEAD1" }} data-testid={`source-${s.id}-view-original`}>
          <ExternalLink size={11} /> View original
        </a>
        <button onClick={reprocess} disabled={busy}
                className="inline-flex items-center gap-1 text-xs border rounded-full px-3 py-1.5 disabled:opacity-50"
                style={{ borderColor: "#DCEAD1" }} data-testid={`source-${s.id}-reprocess`}>
          <RefreshCw size={11} /> Reprocess
        </button>
        {s.status !== "published" && (
          <button onClick={() => setEditorOpen(true)} disabled={busy}
                  className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1.5 text-white disabled:opacity-50"
                  style={{ background: "var(--wofy-primary)" }}
                  data-testid={`source-${s.id}-edit`}>
            <Edit3 size={11} /> Edit & publish
          </button>
        )}
        {s.status !== "not_a_recipe" && (
          <button onClick={reject} disabled={busy}
                  className="inline-flex items-center gap-1 text-xs border rounded-full px-3 py-1.5 disabled:opacity-50"
                  style={{ borderColor: "#DCEAD1", color: "#B04A3E" }} data-testid={`source-${s.id}-reject`}>
            <Ban size={11} /> Not a recipe
          </button>
        )}
        {s.linked_recipe_id && (
          <Link to={`/recipes/${s.linked_recipe_id}`}
                className="inline-flex items-center gap-1 text-xs border rounded-full px-3 py-1.5"
                style={{ borderColor: "#DCEAD1", color: "var(--wofy-primary)" }}>
            View recipe →
          </Link>
        )}
        <button onClick={() => setEnrichOpen(!enrichOpen)}
                className="inline-flex items-center gap-1 text-xs border rounded-full px-3 py-1.5"
                style={{ borderColor: "#DCEAD1" }}
                data-testid={`source-${s.id}-enrich-toggle`}>
          {enrichOpen ? "Close" : "Enrich manually"}
        </button>
        {compact && (
          <button onClick={() => setExpanded(!expanded)}
                  className="ml-auto text-xs opacity-60">{expanded ? "Collapse" : "Expand"}</button>
        )}
      </div>
      {enrichOpen && (
        <ManualEnrichForm source={s} onSaved={() => { setEnrichOpen(false); onChange && onChange(); }} />
      )}
      {editorOpen && (
        <RecipeSourceEditor
          source={s}
          variant="modal"
          onClose={() => setEditorOpen(false)}
          onSaved={() => { onChange && onChange(); }}
          onPublished={() => { setEditorOpen(false); onChange && onChange(); }}
        />
      )}
    </div>
  );
}

function ManualEnrichForm({ source, onSaved }) {
  const [title, setTitle] = useState(source.title || "");
  const [description, setDescription] = useState(source.description || "");
  const [creatorName, setCreatorName] = useState(source.creator_name || "");
  const [creatorHandle, setCreatorHandle] = useState(source.creator_handle || "");
  const [creatorUrl, setCreatorUrl] = useState(source.creator_url || "");
  const [thumbnailUrl, setThumbnailUrl] = useState(source.thumbnail_url || "");
  const [publishedAt, setPublishedAt] = useState(source.published_at || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await http.post(`/recipe-discovery/sources/${source.id}/enrich`, {
        title: title || null,
        description: description || null,
        creator_name: creatorName || null,
        creator_handle: creatorHandle || null,
        creator_url: creatorUrl || null,
        thumbnail_url: thumbnailUrl || null,
        published_at: publishedAt || null,
      });
      onSaved && onSaved();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 rounded-lg border p-3" style={{ borderColor: "#DCEAD1", background: "#F5FAF0" }}
         data-testid={`enrich-form-${source.id}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest opacity-70 mb-2">
        Manual enrichment (fields you set are marked HUMAN_VERIFIED)
      </div>
      {source.fetch_error === "meta_login_wall" && (
        <div className="text-xs mb-3 rounded p-2" style={{ background: "#FEF3C7", color: "#8A5A24" }}>
          <AlertTriangle size={12} className="inline mr-1" />
          Facebook/Meta blocks bot access to this specific video URL. Please copy the title, description,
          and a thumbnail URL from the original post below, or configure <code>SCRAPINGBEE_API_KEY</code>.
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-2">
        <input placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)}
               className="border rounded px-2 py-1.5 text-xs" style={{ borderColor: "#DCEAD1" }}
               data-testid={`enrich-title-${source.id}`} />
        <input placeholder="Thumbnail URL (right-click original video → copy image address)" value={thumbnailUrl} onChange={(e) => setThumbnailUrl(e.target.value)}
               className="border rounded px-2 py-1.5 text-xs" style={{ borderColor: "#DCEAD1" }}
               data-testid={`enrich-thumb-${source.id}`} />
        <input placeholder="Creator name (e.g. Chef Pillai)" value={creatorName} onChange={(e) => setCreatorName(e.target.value)}
               className="border rounded px-2 py-1.5 text-xs" style={{ borderColor: "#DCEAD1" }}
               data-testid={`enrich-creator-${source.id}`} />
        <input placeholder="Creator handle / username" value={creatorHandle} onChange={(e) => setCreatorHandle(e.target.value)}
               className="border rounded px-2 py-1.5 text-xs" style={{ borderColor: "#DCEAD1" }} />
        <input placeholder="Creator page URL" value={creatorUrl} onChange={(e) => setCreatorUrl(e.target.value)}
               className="border rounded px-2 py-1.5 text-xs" style={{ borderColor: "#DCEAD1" }} />
        <input placeholder="Published at (ISO date optional)" value={publishedAt} onChange={(e) => setPublishedAt(e.target.value)}
               className="border rounded px-2 py-1.5 text-xs" style={{ borderColor: "#DCEAD1" }} />
      </div>
      <textarea placeholder="Description (short excerpt only, ≤500 chars)" rows={2}
                value={description} onChange={(e) => setDescription(e.target.value)}
                className="border rounded px-2 py-1.5 text-xs mt-2 w-full" style={{ borderColor: "#DCEAD1" }} />
      <div className="mt-2 flex items-center gap-2">
        <button onClick={save} disabled={busy}
                className="text-xs font-semibold rounded-full px-3 py-1.5 text-white disabled:opacity-50"
                style={{ background: "var(--wofy-primary)" }}
                data-testid={`enrich-save-${source.id}`}>
          {busy ? "Saving…" : "Save enrichment"}
        </button>
        <span className="text-[11px] opacity-70">Fields you leave blank keep their auto-extracted value.</span>
      </div>
    </div>
  );
}

function SourcesTab({ onChange }) {
  const [q, setQ] = useState("");
  const [platform, setPlatform] = useState("");
  const [status, setStatus] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = { limit: 50 };
    if (q) params.q = q;
    if (platform) params.platform = platform;
    if (status) params.status = status;
    http.get("/recipe-discovery/sources", { params })
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .finally(() => setLoading(false));
  };
  useEffect(load, [platform, status]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Search size={14} />
        <input placeholder="Search title, creator, URL…"
               value={q} onChange={(e) => setQ(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter") load(); }}
               className="flex-1 border rounded-full px-4 py-2 text-sm min-w-[280px]"
               style={{ borderColor: "var(--wofy-line)" }}
               data-testid="sources-search-input" />
        <select value={platform} onChange={(e) => setPlatform(e.target.value)}
                className="border rounded-full px-3 py-1.5 text-sm"
                style={{ borderColor: "var(--wofy-line)" }}
                data-testid="sources-filter-platform">
          <option value="">All platforms</option>
          <option value="youtube">YouTube</option>
          <option value="tiktok">TikTok</option>
          <option value="instagram">Instagram</option>
          <option value="facebook">Facebook</option>
          <option value="web">Web</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
                className="border rounded-full px-3 py-1.5 text-sm"
                style={{ borderColor: "var(--wofy-line)" }}
                data-testid="sources-filter-status">
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      {loading ? <div className="text-sm opacity-70">Loading…</div> :
       items.length === 0 ? <div className="text-sm opacity-70">No sources match these filters.</div> :
       <>
         <div className="text-xs opacity-70 mb-3">{total} sources</div>
         <div className="grid gap-3">
           {items.map((s) => <SourceCard key={s.id} s={s} compact onChange={onChange} />)}
         </div>
       </>
      }
    </div>
  );
}

function CreatorsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    http.get("/recipe-discovery/creators", { params: { limit: 60 } })
      .then((r) => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="text-sm opacity-70">Loading…</div>;
  if (!items.length) return <div className="text-sm opacity-70">No creators yet.</div>;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {items.map((c) => (
        <div key={c.id} className="rounded-xl border p-3" style={{ borderColor: "var(--wofy-line)" }}
             data-testid={`creator-${c.id}`}>
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">{c.platform}</div>
          <div className="font-display text-base leading-tight mt-1">{c.creator_name || c.creator_handle || "Unknown"}</div>
          {c.creator_handle && c.creator_handle !== c.creator_name && (
            <div className="text-xs opacity-70">@{c.creator_handle}</div>
          )}
          <div className="text-xs opacity-70 mt-1">{c.sources_count} sources · {c.recipes_count} published</div>
          {c.creator_url && (
            <a href={c.creator_url} target="_blank" rel="noopener noreferrer"
               className="text-xs underline mt-1 inline-block">Visit creator</a>
          )}
        </div>
      ))}
    </div>
  );
}

function PlatformDashboardTab({ queue }) {
  if (!queue) return <div className="text-sm opacity-70">Loading…</div>;
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="rounded-2xl border p-5" style={{ borderColor: "var(--wofy-line)" }}>
        <div className="text-[11px] font-mono uppercase tracking-widest opacity-70 mb-3">By platform</div>
        {Object.entries(queue.by_platform).map(([p, n]) => (
          <div key={p} className="flex items-center justify-between py-2 border-b" style={{ borderColor: "#F2ECDE" }}>
            <span className="capitalize">{PLATFORM_ICON[p] || "◫"} {p}</span>
            <strong>{n}</strong>
          </div>
        ))}
        {Object.keys(queue.by_platform).length === 0 && (
          <div className="text-sm opacity-70">No sources yet.</div>
        )}
      </div>
      <div className="rounded-2xl border p-5" style={{ borderColor: "var(--wofy-line)" }}>
        <div className="text-[11px] font-mono uppercase tracking-widest opacity-70 mb-3">By status</div>
        {Object.entries(queue.by_status).map(([s, n]) => (
          <div key={s} className="flex items-center justify-between py-2 border-b" style={{ borderColor: "#F2ECDE" }}>
            <span className="capitalize" style={{ color: STATUS_TONE[s] || "var(--wofy-text)" }}>
              {STATUS_LABEL[s] || s}
            </span>
            <strong>{n}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}
